// Put-away flow: pick item → backend suggests compatible slots → one click
// commits. The filter checks both status and location.kind because a stale
// TanStack cache can briefly show one field updated but not the other.

import React, { useMemo, useState } from 'react';
import {
  Card,
  ListGroup,
  Badge,
  Button,
  Alert,
  Spinner,
  Form,
} from 'react-bootstrap';

import { useItems, useSuggestedSlots, usePutAway } from '../../features/warehouse/useItems.js';
import {
  typeBadgeColor,
  extractErrorMessage,
  matchesTypeFilter,
  bySerialAsc,
  useFlashBanner,
} from '../../features/warehouse/warehouseUtils.js';

export default function UnplacedItemsPanel({ buildingId }) {
  const { data: items, isLoading, error } = useItems();
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const { banner, flash } = useFlashBanner();

  const putAway = usePutAway();
  const { data: suggestions, isLoading: loadingSuggestions } = useSuggestedSlots(
    selectedSerial,
    buildingId
  );

  const unplaced = useMemo(() => {
    if (!items) return [];
    return items
      .filter(it => {
        if (it.status === 'shipped' || it.status === 'scrapped' || it.status === 'loaded')
          return false;
        const kind = it.location?.kind;
        if (kind === 'slot' || kind === 'truck' || kind === 'shipped') return false;
        return matchesTypeFilter(it, filterType);
      })
      .sort(bySerialAsc);
  }, [items, filterType]);

  const handlePutAway = (slotCode) => {
    if (!selectedSerial) return;
    putAway.mutate(
      { serial: selectedSerial, slotCode },
      {
        onSuccess: () => {
          flash('success', `${selectedSerial} → ${slotCode}`);
          setSelectedSerial(null);
        },
        onError: (err) =>
          flash('danger', extractErrorMessage(err, 'Put-away failed'), { autoHide: false }),
      }
    );
  };

  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>To put away</strong>
        <Badge bg="secondary">{unplaced.length}</Badge>
      </Card.Header>

      <Card.Body className="p-2" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {banner && (
          <Alert variant={banner.variant} className="py-1 px-2 mb-2 small">
            {banner.text}
          </Alert>
        )}

        <Form.Select
          size="sm"
          className="mb-2"
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          <option value="all">All types</option>
          <option value="drum">Drums</option>
          <option value="europallet">Europallets</option>
          <option value="coil">Coils</option>
        </Form.Select>

        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {error && (
          <Alert variant="danger" className="small">
            {error.message}
          </Alert>
        )}

        {!isLoading && unplaced.length === 0 && (
          <div className="text-muted small text-center py-3">
            Nothing waiting. 👍
          </div>
        )}

        <ListGroup variant="flush">
          {unplaced.map(item => {
            const isSelected = item.serial === selectedSerial;
            return (
              <React.Fragment key={item._id}>
                <ListGroup.Item
                  action
                  active={isSelected}
                  onClick={() => setSelectedSerial(isSelected ? null : item.serial)}
                  className="px-2 py-2"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold small">{item.serial}</div>
                      <div className="small text-muted">
                        {item.cableName} · {item.lengthMeters}m
                      </div>
                    </div>
                    <Badge bg={typeBadgeColor(item.type)}>{item.type}</Badge>
                  </div>
                  {item.status === 'reserved' && (
                    <div className="small text-primary mt-1">reserved</div>
                  )}
                </ListGroup.Item>

                {isSelected && (
                  <div className="p-2 bg-light border-bottom">
                    <div className="small fw-bold mb-1">Suggested slots</div>
                    {loadingSuggestions && <Spinner animation="border" size="sm" />}
                    {!loadingSuggestions && (!suggestions || suggestions.length === 0) && (
                      <div className="small text-muted">
                        No compatible empty slot found.
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-1">
                      {suggestions?.slice(0, 6).map(s => (
                        <Button
                          key={s.code}
                          size="sm"
                          variant="outline-success"
                          disabled={putAway.isPending}
                          onClick={() => handlePutAway(s.code)}
                        >
                          {s.code}
                        </Button>
                      ))}
                    </div>
                    {putAway.isPending && (
                      <div className="small text-muted mt-1">
                        <Spinner animation="border" size="sm" /> Putting away…
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </ListGroup>
      </Card.Body>
    </Card>
  );
}

